/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { CustomFieldResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import BaseComponent from "~/components/BaseComponent/index";
import type { DoBusyTask } from "~/components/DataBaseComponent/DataBaseComponent";
import { CustomFieldSelect } from "~/components/form/CustomeField/CustomFieldSelect";
import type FormFieldProps from "~/components/form/FormFieldProps";
import * as tenantCustomFields from "~/components/tenantCustomFields";
interface TenantCustomFieldSelectorProps extends FormFieldProps<string[]> {
    customFields?: CustomFieldResource[];
    doBusyTask: DoBusyTask;
    autoFocus?: boolean;
}
interface TenantCustomFieldSelectorState {
    customFieldIdToSelectedCustomFieldValueIds: Map<string, string[]>;
    customFields: CustomFieldResource[] | null;
}
const parseSelectedCustomFieldValueIdsFromCanonicalCustomFieldValues = (customFields: CustomFieldResource[], canonicalCustomFieldValues: string[]): Map<string, string[]> => {
    const customFieldIdToSelectedCustomFieldValueIds = new Map<string, string[]>();
    customFields.forEach((customField) => {
        const fieldValueIds: string[] = [];
        customField.Values?.forEach((value) => {
            if (value.CanonicalCustomFieldValueName && canonicalCustomFieldValues.includes(value.CanonicalCustomFieldValueName)) {
                fieldValueIds.push(value.Id);
            }
        });
        customFieldIdToSelectedCustomFieldValueIds.set(customField.Id, fieldValueIds);
    });
    return customFieldIdToSelectedCustomFieldValueIds;
};
const getCanonicalCustomFieldValuesFromSelectedCustomFieldValueIds = (customFields: CustomFieldResource[], customFieldIdToCustomFieldValueIds: Map<string, string[]>): string[] => {
    const canonicalCustomFieldValues: string[] = [];
    customFields.forEach((customField) => {
        const fieldValueIds = customFieldIdToCustomFieldValueIds.get(customField.Id) || [];
        fieldValueIds.forEach((valueId) => {
            const value = customField.Values?.find((v) => v.Id === valueId);
            if (value?.CanonicalCustomFieldValueName) {
                canonicalCustomFieldValues.push(value.CanonicalCustomFieldValueName);
            }
        });
    });
    return canonicalCustomFieldValues;
};
export class TenantCustomFieldSelector extends BaseComponent<TenantCustomFieldSelectorProps, TenantCustomFieldSelectorState> {
    constructor(props: TenantCustomFieldSelectorProps) {
        super(props);
        const customFields = this.props.customFields?.sort((f1, f2) => f1.SortOrder - f2.SortOrder) || [];
        this.state = {
            customFieldIdToSelectedCustomFieldValueIds: parseSelectedCustomFieldValueIdsFromCanonicalCustomFieldValues(customFields, props.value || []),
            customFields: null,
        };
    }
    async componentDidMount() {
        if (!this.props.customFields?.length) {
            await this.props.doBusyTask(async () => {
                const customFields = await tenantCustomFields.getAllTenantCustomFields();
                const sortedCustomFields = customFields.sort((f1, f2) => f1.SortOrder - f2.SortOrder);
                this.setState({
                    customFields: sortedCustomFields,
                    customFieldIdToSelectedCustomFieldValueIds: parseSelectedCustomFieldValueIdsFromCanonicalCustomFieldValues(sortedCustomFields, this.props.value || []),
                });
            });
        }
    }
    componentDidUpdate(prevProps: TenantCustomFieldSelectorProps) {
        if (prevProps.value !== this.props.value) {
            const customFields = this.props.customFields?.sort((f1, f2) => f1.SortOrder - f2.SortOrder) || this.state.customFields || [];
            this.setState({
                customFieldIdToSelectedCustomFieldValueIds: parseSelectedCustomFieldValueIdsFromCanonicalCustomFieldValues(customFields, this.props.value || []),
            });
        }
    }
    handleCustomFieldChange = (customFieldId: string, newValues: string[]) => {
        const updatedMap = new Map(this.state.customFieldIdToSelectedCustomFieldValueIds);
        updatedMap.set(customFieldId, newValues);
        this.setState({
            customFieldIdToSelectedCustomFieldValueIds: updatedMap,
        }, () => {
            const customFields = this.props.customFields?.sort((f1, f2) => f1.SortOrder - f2.SortOrder) || this.state.customFields || [];
            const canonicalValues = getCanonicalCustomFieldValuesFromSelectedCustomFieldValueIds(customFields, updatedMap);
            this.props.onChange?.(canonicalValues);
        });
    };
    render() {
        const customFields = this.props.customFields?.sort((f1, f2) => f1.SortOrder - f2.SortOrder) || this.state.customFields || [];
        if (!customFields.length) {
            return null;
        }
        return (<div>
                {customFields.map((customField, index) => {
                if (!customField.Values || customField.Values.length === 0) {
                    return null;
                }
                const fieldValues = this.state.customFieldIdToSelectedCustomFieldValueIds.get(customField.Id) || [];
                return <CustomFieldSelect key={customField.Id} customField={customField} value={fieldValues} onChange={(newValues) => this.handleCustomFieldChange(customField.Id, newValues)} autoFocus={this.props.autoFocus && index === 0}/>;
            })}
            </div>);
    }
    static displayName = "TenantCustomFieldSelector";
}
